import { useState, useRef, useEffect } from 'react';
import { Trash2, Pencil, AlertTriangle, Loader2, MoreVertical, FolderOpen } from 'lucide-react';
import { Tooltip } from '../../Tooltip';
import { IconButton } from '../../Button';

interface AssetThumbnailProps {
  src: string;
  name: string;
  size?: number;
  mimeType?: string;
  hasWarning?: boolean;
  warningText?: string;
  showControls: boolean;
  isPending?: boolean;
  pendingStatus?: 'validating' | 'uploading' | 'done' | 'error';
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  onClick?: () => void;
  onDelete?: () => void;
  onRename?: (newName: string) => void;
  onChangeCategory?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
}

export const AssetThumbnail = ({
  src, name, mimeType, hasWarning, warningText, showControls,
  isPending, pendingStatus, selectable, selected, onSelect,
  onClick, onDelete, onRename, onChangeCategory, onDragStart,
}: AssetThumbnailProps) => {
  const isVector = mimeType === 'image/svg+xml';
  const [isRenaming, setIsRenaming] = useState(false);
  const [editName, setEditName] = useState(name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) { setMenuOpen(false); setConfirmingDelete(false); }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleRenameSubmit = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== name) onRename?.(trimmed);
    setIsRenaming(false);
  };

  return (
    <div
      className={`group relative p-2 border rounded-xl transition-colors cursor-pointer ${
        selected
          ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/30'
          : 'bg-gray-50 border-gray-200 hover:border-blue-400 hover:bg-blue-50/50'
      }`}
      onClick={() => {
        if (isRenaming || isPending || menuOpen) return;
        if (selectable) {
          onSelect?.(!selected);
        } else {
          onClick?.();
        }
      }}
      draggable={!isPending && !isRenaming && !selectable}
      onDragStart={onDragStart}
    >
      {/* Selection checkbox */}
      {selectable && !isPending && (
        <div className="absolute top-1.5 left-1.5 z-10">
          <input
            type="checkbox"
            checked={selected ?? false}
            onChange={(e) => { e.stopPropagation(); onSelect?.(!selected); }}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        </div>
      )}

      {/* Image */}
      <div className="relative aspect-square flex items-center justify-center overflow-hidden rounded-lg bg-size-[16px_16px] bg-[repeating-conic-gradient(#e5e7eb_0%_25%,white_0%_50%)]">
        <img src={src} alt={name} loading="lazy" crossOrigin="anonymous" className="max-w-[75%] max-h-[75%] object-contain" />

        {/* Pending overlay */}
        {isPending && (
          <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <span className="text-[10px] text-gray-500 mt-1">
              {pendingStatus === 'validating' ? 'Checking...' : 'Uploading...'}
            </span>
          </div>
        )}

        {/* Warning badge with tooltip */}
        {hasWarning && !isPending && (
          <div className="absolute top-1.5 left-1.5" onClick={(e) => e.stopPropagation()}>
            {warningText ? (
              <Tooltip content={<span className="max-w-[12rem] block">{warningText}</span>} side="bottom" align="start">
                <div><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /></div>
              </Tooltip>
            ) : (
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            )}
          </div>
        )}

        {/* Vector badge */}
        {isVector && !isPending && (
          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-semibold rounded-md leading-tight">
            SVG
          </div>
        )}
      </div>

      {/* Name row with dots menu */}
      <div className="flex items-center mt-1.5 gap-1">
        {isRenaming ? (
          <input
            autoFocus
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(); if (e.key === 'Escape') setIsRenaming(false); }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 text-xs px-1.5 py-0.5 border border-blue-300 rounded outline-none focus:ring-1 focus:ring-blue-400"
          />
        ) : (
          <>
            <p className="flex-1 min-w-0 text-xs text-gray-500 truncate">{name}</p>
            {showControls && !isPending && (
              <div ref={menuRef} className="relative flex-shrink-0">
                <IconButton
                  size="xs"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(prev => !prev); }}
                  className="bg-gray-100 hover:bg-gray-200"
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </IconButton>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
                    {confirmingDelete ? (
                      <div className="px-3 py-2">
                        <p className="text-xs text-gray-700 mb-2">Delete this image?</p>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setConfirmingDelete(false); onDelete?.(); }}
                            className="flex-1 px-2 py-1 text-xs text-white bg-red-500 hover:bg-red-600 rounded transition-colors"
                          >
                            Delete
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmingDelete(false); }}
                            className="flex-1 px-2 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setEditName(name); setIsRenaming(true); }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          <Pencil className="w-3 h-3" />
                          Rename
                        </button>
                        {onChangeCategory && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onChangeCategory(); }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                          >
                            <FolderOpen className="w-3 h-3" />
                            Change Category
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
